import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import calendar from 'assets/icons/calendar.svg';
import clock from 'assets/icons/clock.svg';
import locationIcon from 'assets/icons/location.svg';
import usersIcon from 'assets/icons/users.svg';
import racquet from 'assets/icons/racquet.svg';
import infoSquare from 'assets/icons/infoSquare.svg';
import copy from 'assets/icons/copy.svg';
import { SuccessToast, ErrorToast } from 'utils/Toasts';
import Button from 'components/Button';
import { post, getUserIdFromToken, remove } from 'api';
import avatarGrey from 'assets/images/defaultAvatar-grey.png';
import { Modal } from '@mui/material';
import { Delete } from '@mui/icons-material';

export default function PartnerCard({
  matchId,
  avatar,
  name,
  ntrp,
  date,
  time,
  location,
  tennisCourt,
  details,
  page,
  fetchMatches,
  userId,
  mailTo,
  hideButtons,
  matchData,
  type,
  players,
  maxPlayers,
  playersRegistered,
  regOpen,
  duration,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const successToastRef = useRef();
  const errorToastRef = useRef();
  const currentUserId = getUserIdFromToken();
  const isMyMatch = currentUserId === userId;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const dateMatch = moment(date, 'YYYY-MM-DD');
  const timeMatch = moment(time, 'HH:mm:ss');
  const dateTimeMatch = moment(
    `${dateMatch.format('YYYY-MM-DD')}T${timeMatch.format('HH:mm:ss')}`,
  );
  const isDateTimeAfter = moment().isAfter(dateTimeMatch);

  const [hours, minutes] = duration.split(':').map(Number);
  const updatedTime = timeMatch
    .clone()
    .add(hours, 'hours')
    .add(minutes, 'minutes');

  const isCurrentUserRegistered = playersRegistered?.some(
    (player) => player.user_id === currentUserId,
  );

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleConnect = async () => {
    const body = {
      user_id: currentUserId,
    };
    const response = await post(`/matches/register/${matchId}`, body);
    const data = await response.json();
    if (data.status === 'SUCCESS') {
      successToastRef.current.showSuccess(t('matchConfirmed'));
      fetchMatches();
      // setTimeout(() => {
      //   navigate('/info');
      // }, 2000);
    } else {
      errorToastRef.current.showError(data.err);
    }
  };

  const confirmConnection = () => {
    confirmAlert({
      title: '',
      message: t('confirmConnection'),
      buttons: [
        {
          label: t('yes'),
          onClick: () => handleConnect(),
        },
        {
          label: t('no'),
        },
      ],
    });
  };

  const handleDelete = async () => {
    const response = await remove(`/matches/matches/${matchId}`);
    const data = await response.json();
    if (data.status === 'SUCCESS') {
      successToastRef.current.showSuccess(t('matchDeleted'));
      setTimeout(() => {
        fetchMatches();
      }, 1500);
    } else {
      errorToastRef.current.showError(data.err);
    }
  };

  const confirmDelete = () => {
    confirmAlert({
      title: '',
      message: t('confirmDeleteMatch'),
      buttons: [
        {
          label: t('yes'),
          onClick: () => handleDelete(),
        },
        {
          label: t('no'),
        },
      ],
    });
  };

  const handleRemovePlayer = async (userId) => {
    const response = await post(`/matches/unregister/${matchId}`, {
      user_id: userId,
    });
    const data = await response.json();
    if (data.status === 'SUCCESS') {
      successToastRef.current.showSuccess(t('playerRemoved'));
      setTimeout(() => {
        fetchMatches();
      }, 1500);
    } else {
      errorToastRef.current.showError(data.err);
    }
  };

  const confirmRemovePlayer = (userId) => {
    confirmAlert({
      title: '',
      message: t('confirmRemovePlayer'),
      buttons: [
        {
          label: t('yes'),
          onClick: () => handleRemovePlayer(userId),
        },
        {
          label: t('no'),
        },
      ],
    });
  };

  const handleCancelation = async () => {
    const body = {
      user_id: currentUserId,
    };
    const response = await post(`/matches/unregister/${matchId}`, body);
    const data = await response.json();
    if (data.status === 'SUCCESS') {
      successToastRef.current.showSuccess(t('matchCanceled'));
      setTimeout(() => {
        fetchMatches();
      }, 1500);
    } else {
      errorToastRef.current.showError(data.err);
    }
  };

  const confirmCancelation = () => {
    confirmAlert({
      title: '',
      message: t('confirmCancelMatch'),
      buttons: [
        {
          label: t('yes'),
          onClick: () => handleCancelation(),
        },
        {
          label: t('no'),
        },
      ],
    });
  };

  return (
    <Card
      sx={{
        maxWidth: { xs: '100%', sm: '416px' },
        minHeight: { xs: '100%', sm: hideButtons ? 'auto' : '432px' },
        maxHeight: 'auto',
        width: '100%',
        borderRadius: '16px',
        padding: { xs: '24px', sm: '32px' },
      }}
    >
      <SuccessToast ref={successToastRef} />
      <ErrorToast ref={errorToastRef} />
      <CardContent
        sx={{
          padding: 0,
          minHeight: { xs: '100%', sm: hideButtons ? 'auto' : '288px' },
          '&:last-child': { paddingBottom: 0 },
        }}
        height={'100%'}
      >
        <Box
          display={'flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          <Box display={'flex'} alignItems={'center'}>
            <Box display={'flex'} height={60} width={60}>
              <Box
                component={'img'}
                src={
                  avatar
                    ? `${process.env.REACT_APP_BACKEND_URL_BASE}/public/images/${avatar}`
                    : avatarGrey
                }
                alt="Author avatar"
                height={60}
                width={60}
                borderRadius={'50%'}
                sx={{
                  objectFit: 'cover',
                }}
              />
            </Box>
            <Box
              component={'a'}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                  textDecorationColor: theme.palette.darkGrey[700],
                },
              }}
              href={`${window.location.origin}/info/${userId}`}
            >
              <Typography
                variant={'body3'}
                fontWeight={600}
                marginLeft={'16px'}
                color={theme.palette.darkGrey[700]}
              >
                {name}
              </Typography>
            </Box>
          </Box>
          <Box
            height={'28px'}
            backgroundColor={theme.palette.darkGrey[100]}
            borderRadius={'40px'}
            padding={'4px 20px'}
            display={'flex'}
            alignItems={'center'}
          >
            <Typography
              variant={'body5'}
              fontWeight={600}
              color={theme.palette.orange[700]}
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {type === 'Vienetai'
                ? t('singles')
                : type === 'Dvejetai'
                ? t('doubles')
                : t('mixed')}
              &nbsp;
            </Typography>
            <Typography
              variant={'body5'}
              fontWeight={600}
              color={theme.palette.orange[700]}
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {ntrp}
            </Typography>
          </Box>
        </Box>
        <Box marginTop={'20px'} flexDirection={'column'}>
          <Box display={'flex'} alignItems={'center'}>
            <Box
              component={'img'}
              src={calendar}
              height={1}
              borderRadius={'50%'}
              alt="calendar"
            />
            <Typography
              variant={'body3'}
              fontWeight={600}
              marginLeft={'8px'}
              color={theme.palette.darkGrey[700]}
            >
              {t('date')}:
            </Typography>
            <Typography
              variant={'body3'}
              marginLeft={'4px'}
              color={theme.palette.darkGrey[600]}
            >
              {date}
            </Typography>
          </Box>
          <Box display={'flex'} alignItems={'center'} marginTop={'10px'}>
            <Box
              component={'img'}
              src={clock}
              height={1}
              borderRadius={'50%'}
              alt="clock"
            />
            <Typography
              variant={'body3'}
              fontWeight={600}
              marginLeft={'8px'}
              color={theme.palette.darkGrey[700]}
            >
              {t('time')}:
            </Typography>
            <Typography
              variant={'body3'}
              marginLeft={'4px'}
              color={theme.palette.darkGrey[600]}
            >
              {time === '23:59:00'
                ? t('anyTime')
                : moment(time, 'HH:mm:ss').format('HH:mm')}{' '}
              - {updatedTime.format('HH:mm')}
            </Typography>
          </Box>
          <Box display={'flex'} alignItems={'center'} marginTop={'10px'}>
            <Box
              component={'img'}
              src={locationIcon}
              height={1}
              borderRadius={'50%'}
              alt="location"
            />
            <Typography
              variant={'body3'}
              fontWeight={600}
              marginLeft={'8px'}
              color={theme.palette.darkGrey[700]}
            >
              {t('location')}:
            </Typography>
            <Typography
              variant={'body3'}
              marginLeft={'4px'}
              color={theme.palette.darkGrey[600]}
            >
              {location}
            </Typography>
          </Box>
          <Box display={'flex'} alignItems={'flex-start'} marginTop={'10px'}>
            <Box
              component={'img'}
              src={racquet}
              height={1}
              borderRadius={'50%'}
              alt="racquet"
            />
            <Typography
              variant={'body3'}
              fontWeight={600}
              marginLeft={'8px'}
              color={theme.palette.darkGrey[700]}
            >
              {t('court')}:
            </Typography>
            <Typography
              variant={'body3'}
              marginLeft={'4px'}
              color={theme.palette.darkGrey[600]}
              sx={{
                wordBreak: 'break-word',
              }}
            >
              {!tennisCourt ? t('notSelected') : tennisCourt}
            </Typography>
          </Box>
          <Box display={'flex'} alignItems={'center'} marginTop={'10px'}>
            <Box
              component={'img'}
              src={usersIcon}
              height={1}
              borderRadius={'50%'}
              alt="players"
            />
            <Typography
              variant={'body3'}
              fontWeight={600}
              marginLeft={'8px'}
              color={theme.palette.darkGrey[700]}
            >
              {t('players')}:
            </Typography>
            <Typography
              variant={'body3'}
              marginLeft={'4px'}
              color={theme.palette.darkGrey[600]}
            >
              {players} / {maxPlayers}
            </Typography>
          </Box>
          {details && (
            <Box
              display={'flex'}
              alignItems={'flex-start'}
              marginTop={'10px'}
              flexDirection={'column'}
            >
              <Box display={'flex'} alignItems={'center'} mb={'4px'}>
                <Box
                  component={'img'}
                  src={infoSquare}
                  height={1}
                  borderRadius={'50%'}
                  alt="info"
                />
                <Typography
                  variant={'body3'}
                  fontWeight={600}
                  marginLeft={'8px'}
                  color={theme.palette.darkGrey[700]}
                >
                  {t('details')}:
                </Typography>
              </Box>
              <Typography
                variant={'body3'}
                marginLeft={'4px'}
                color={theme.palette.darkGrey[600]}
              >
                {details}
              </Typography>
            </Box>
          )}
          <Box
            component={'a'}
            sx={{
              width: 'full',
              height: '40px',
              marginTop: '10px',
              display: 'flex',
              cursor: 'pointer',
              border: `1px solid ${theme.palette.darkGrey[300]}`,
              borderRadius: '8px',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: theme.palette.darkGrey[100],
              },
            }}
            onClick={() => {
              navigator.clipboard.writeText(
                `${window.location.href}/${matchId}`,
              );
              setCopied(true);
              setTimeout(() => {
                setCopied(false);
              }, 1000);
            }}
          >
            <Box
              component={'img'}
              src={copy}
              alt="copy"
              width={'20px'}
              height={'20px'}
            />
            <Typography
              variant="body3"
              sx={{
                color: theme.palette.darkGrey[700],
                marginLeft: '8px',
                fontWeight: 600,
              }}
            >
              {copied ? t('copied') : t('copyLink')}
            </Typography>
          </Box>
        </Box>
      </CardContent>
      {(!hideButtons || hideButtons === 'undefined') && (
        <>
          {page === 'overview' && (
            <Box marginTop="32px">
              {!isDateTimeAfter && (
                <Button
                  width={'100%'}
                  buttontype="orange"
                  onClick={() =>
                    navigate('/siusti-zinute', {
                      state: {
                        userId,
                        date,
                        time,
                        tennisCourt,
                        name,
                        avatar,
                        ntrp,
                        location,
                        mailTo,
                        mailSubject: `Pasižaidimas ${date}`,
                        isTournament: false,
                      },
                    })
                  }
                >
                  {t('sendMessage')}
                </Button>
              )}
              <Button
                marginTop={'12px'}
                width={'100%'}
                buttontype="white"
                bordered="true"
                onClick={() => confirmCancelation()}
              >
                {t('cancelConnection')}
              </Button>
            </Box>
          )}
          {page === 'find-partner' && (
            <Box marginTop="32px">
              {isMyMatch && (
                <Button
                  width={'100%'}
                  buttontype="orange"
                  onClick={() => confirmDelete()}
                >
                  {t('delete')}
                </Button>
              )}
              {!isCurrentUserRegistered && regOpen && (
                <Button
                  marginTop={'12px'}
                  width={'100%'}
                  buttontype="orange"
                  onClick={() => confirmConnection()}
                >
                  {t('connect')}
                </Button>
              )}
              {isCurrentUserRegistered && (
                <Button
                  marginTop={'12px'}
                  width={'100%'}
                  buttontype="orange"
                  onClick={() => confirmCancelation()}
                >
                  {t('cancelConnection')}
                </Button>
              )}
              <Button
                marginTop={'12px'}
                width={'100%'}
                buttontype="white"
                bordered="true"
                onClick={handleOpenModal}
              >
                {t('showPlayers')}
              </Button>
              {!isMyMatch && (
                <Button
                  marginTop={'12px'}
                  width={'100%'}
                  buttontype="white"
                  bordered="true"
                  onClick={() =>
                    navigate('/siusti-zinute', {
                      state: {
                        userId,
                        date,
                        time,
                        tennisCourt,
                        name,
                        avatar,
                        ntrp,
                        location,
                        mailTo,
                        duration,
                        mailSubject: `Pasižaidimas ${date}`,
                        isTournament: false,
                        type,
                      },
                    })
                  }
                >
                  {t('sendMessage')}
                </Button>
              )}
            </Box>
          )}
          {page !== 'overview' && page !== 'find-partner' && (
            <Box marginTop="32px">
              {regOpen && !isCurrentUserRegistered && (
                <Button
                  width={'100%'}
                  buttontype="orange"
                  onClick={() =>
                    navigate('/pasizaidimai', {
                      state: { to: '/pasizaidimai' },
                    })
                  }
                >
                  {t('connect')}
                </Button>
              )}
              <Button
                marginTop={'12px'}
                width={'100%'}
                buttontype="white"
                bordered="true"
                onClick={handleOpenModal}
              >
                {t('showPlayers')}
              </Button>
            </Box>
          )}
        </>
      )}
      {/* Modal for showing players */}
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '400px' },
            bgcolor: 'background.paper',
            border: `2px solid ${theme.palette.grey[300]}`,
            boxShadow: 24,
            p: 4,
            borderRadius: '8px',
          }}
        >
          <Typography variant="h6" mb={2}>
            {t('registeredPlayers')}
          </Typography>
          <Box>
            {playersRegistered?.length > 0 ? (
              playersRegistered.map((player, index) => (
                <Box
                  key={player.user_id}
                  display="flex"
                  alignItems="center"
                  mb={2}
                >
                  <Box
                    component="img"
                    src={
                      player.image
                        ? `${process.env.REACT_APP_BACKEND_URL_BASE}/public/images/${player.image}`
                        : avatarGrey
                    }
                    alt="Player avatar"
                    sx={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      marginRight: '12px',
                    }}
                  />
                  <Typography
                    variant="body2"
                    color={theme.palette.darkGrey[700]}
                  >
                    {player.first_name} {player.last_name}
                  </Typography>
                  <Delete
                    sx={{
                      color: theme.palette.darkGrey[600],
                      marginLeft: 'auto',
                      cursor: 'pointer',
                      '&:hover': {
                        color: theme.palette.error[500],
                      },
                      display: isMyMatch ? 'block' : 'none',
                    }}
                    onClick={() => confirmRemovePlayer(player.user_id)}
                  />
                </Box>
              ))
            ) : (
              <Typography variant="body2" color={theme.palette.darkGrey[600]}>
                {t('noPlayersRegistered')}
              </Typography>
            )}
          </Box>
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button
              buttontype="white"
              bordered="true"
              onClick={handleCloseModal}
            >
              {t('close')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Card>
  );
}
